/** The Check tab: the server's validator (design §14 — the ONE authoritative
    checker, also run at save and at publish) over the model as it is on
    screen, each finding linked to its step, and the writable set — exactly
    which columns a save through this wizard may change (§12.3). */

import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, XCircle } from 'lucide-react'
import { parseApiError } from '@twasta/page-runtime/txn'
import { checkWizard } from '@/api/wizards'
import type { WizardCheckIssue, WizardCheckResult } from '@/api/wizards'
import { useWizardDesign } from './context'
import { Badge, SectionHeader, cardStyle, emptyHintStyle, hintStyle, smallBtn } from './ui'

function IssueRow({ issue, tone, onGo }: {
  issue: WizardCheckIssue; tone: 'error' | 'warn'; onGo: (step: string) => void
}) {
  return (
    <div style={{
      display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 4px',
      borderBottom: '1px solid var(--color-border)', fontSize: 13.5,
    }}>
      {tone === 'error'
        ? <XCircle size={14} style={{ color: 'var(--color-error, #d33)', flexShrink: 0, marginTop: 2 }} />
        : <AlertTriangle size={14} style={{ color: 'var(--color-warning, #b45309)', flexShrink: 0, marginTop: 2 }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div>{issue.message}</div>
        <div style={{ ...hintStyle, marginTop: 1 }}>
          <code>{issue.code}</code>{issue.path ? <> · <code>{issue.path}</code></> : null}
        </div>
      </div>
      {issue.step && (
        <button type="button" style={smallBtn} onClick={() => onGo(issue.step!)}>Go to {issue.step}</button>
      )}
    </div>
  )
}

export default function CheckTab({ onGoToStep, lastSaveCheck }: {
  onGoToStep: (step: string) => void
  /** The check returned with the last save, shown until a fresh one runs. */
  lastSaveCheck: WizardCheckResult | null
}) {
  const { project, model } = useWizardDesign()
  const [result, setResult] = useState<WizardCheckResult | null>(lastSaveCheck)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      setResult(await checkWizard(project, model))
    } catch (err) {
      setError(parseApiError(err).message || 'The check could not run')
    } finally {
      setBusy(false)
    }
  }, [project, model])

  // A fresh check every time the tab opens; the model may have changed since.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void run() }, [])

  const writable = result?.writable
  const columns = Object.entries(writable?.columns || {})
  return (
    <div>
      <div style={cardStyle}>
        <SectionHeader right={(
          <button type="button" style={smallBtn} onClick={run} disabled={busy}>
            {busy ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={12} />}
            Check again
          </button>
        )}>
          Findings
        </SectionHeader>
        {error && <div style={{ ...hintStyle, color: 'var(--color-error, #d33)' }}>{error}</div>}
        {result && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            {result.errors.length === 0
              ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13.5, color: 'var(--color-success, #15803d)' }}>
                <CheckCircle2 size={14} /> No errors
              </span>
              : <Badge tone="error">{result.errors.length} error{result.errors.length === 1 ? '' : 's'}</Badge>}
            {result.warnings.length > 0 && <Badge tone="warn">{result.warnings.length} warning{result.warnings.length === 1 ? '' : 's'}</Badge>}
          </div>
        )}
        {result?.errors.map((iss, i) => <IssueRow key={`e${i}`} issue={iss} tone="error" onGo={onGoToStep} />)}
        {result?.warnings.map((iss, i) => <IssueRow key={`w${i}`} issue={iss} tone="warn" onGo={onGoToStep} />)}
        {!result && !busy && !error && <div style={emptyHintStyle}>Not checked yet.</div>}
      </div>

      <div style={cardStyle}>
        <SectionHeader>Writable set — what a save through this wizard may change</SectionHeader>
        {!writable ? (
          <div style={emptyHintStyle}>Run the check to see it.</div>
        ) : (
          <>
            {columns.length === 0 && <div style={emptyHintStyle}>No columns — the wizard sets nothing.</div>}
            {columns.map(([formNo, cols]) => (
              <div key={formNo} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 3 }}>Form {formNo}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {(cols || []).map((c) => (
                    <code key={c} style={{
                      fontSize: 13, padding: '1px 6px', borderRadius: 8,
                      background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
                    }}>{c}</code>
                  ))}
                </div>
              </div>
            ))}
            <div style={{ fontSize: 13.5, marginTop: 4 }}>
              Lines may be added on: <strong>{(writable.add_lines || []).join(', ') || 'none'}</strong>
              {' · '}deleted on: <strong>{(writable.delete_lines || []).join(', ') || 'none'}</strong>
            </div>
            <div style={hintStyle}>
              Any other column must be saved unchanged, or the save is refused (WIZARD_FIELD_LOCKED). A column filled by a
              business-logic or REST item change belongs in “Also writes” on Base &amp; Mode.
            </div>
          </>
        )}
      </div>
    </div>
  )
}
